/**
 * Content Repurposing Service
 * Handles repurposing long-form content into social posts
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/structured-logger';
import type { GenerationConfig } from '@/types/sequence-data';

const serviceLogger = logger.child({ service: 'ContentRepurposingService' });

export interface RepurposeContentParams {
  organizationId: string;
  sourceType: 'webinar' | 'blog' | 'video' | 'podcast' | 'whitepaper' | 'case_study' | 'document';
  sourceUrl?: string;
  sourceContent?: string;
  sourceTitle: string;
  postCount?: number;
  platforms?: string[];
  includeHashtags?: boolean;
}

export interface RepurposedContent {
  id: string;
  organization_id: string;
  source_type: string;
  source_url: string | null;
  source_title: string | null;
  source_summary: string | null;
  key_quotes: string[] | null;
  key_statistics: string[] | null;
  generated_post_ids: string[] | null;
  generation_config: GenerationConfig | null;
  created_at: string;
  created_by: string | null;
}

export const ContentRepurposingService = {
  /**
   * Repurpose content into social posts
   */
  async repurposeContent(params: RepurposeContentParams) {
    try {
      const { data, error } = await supabase.functions.invoke('repurpose-content', {
        body: params
      });

      if (error) throw error;
      return data;
    } catch (error) {
      serviceLogger.error('Failed to repurpose content', { error, params });
      throw error;
    }
  },

  /**
   * Get all repurposed content for an organization
   */
  async getRepurposedContent(organizationId: string): Promise<RepurposedContent[]> {
    try {
      const { data, error } = await supabase
        .from('repurposed_content')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        generation_config: item.generation_config as unknown as GenerationConfig | null,
      }));
    } catch (error) {
      serviceLogger.error('Failed to fetch repurposed content', { error, organizationId });
      throw error;
    }
  },

  /**
   * Get repurposed content by source
   */
  async getRepurposedContentBySource(sourceId: string): Promise<RepurposedContent | null> {
    try {
      const { data, error } = await supabase
        .from('repurposed_content')
        .select('*')
        .eq('source_content_id', sourceId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      return {
        ...data,
        generation_config: data.generation_config as unknown as GenerationConfig | null,
      };
    } catch (error) {
      serviceLogger.error('Failed to fetch repurposed content by source', { error, sourceId });
      throw error;
    }
  },

  /**
   * Get repurposing statistics
   */
  async getRepurposingStats(organizationId: string) {
    try {
      const { data, error } = await supabase
        .from('repurposed_content')
        .select('source_type, generated_post_ids')
        .eq('organization_id', organizationId);

      if (error) throw error;

      const stats = {
        totalRepurposed: data?.length || 0,
        totalPostsGenerated: data?.reduce((sum, item) => sum + (item.generated_post_ids?.length || 0), 0) || 0,
        bySourceType: data?.reduce((acc: Record<string, number>, item) => {
          acc[item.source_type] = (acc[item.source_type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) || {}
      };

      return stats;
    } catch (error) {
      serviceLogger.error('Failed to fetch repurposing stats', { error, organizationId });
      throw error;
    }
  },

  /**
   * Delete repurposed content
   */
  async deleteRepurposedContent(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('repurposed_content')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      serviceLogger.error('Failed to delete repurposed content', { error, id });
      throw error;
    }
  }
};
